import { Badge, Card, Descriptions, List, Space, Tag, Typography } from "antd";
import {
  DollarSign,
  FileText,
} from "lucide-react";
import React, { useMemo } from "react";
import { TBooking, TServiceTicket } from "~/types";
import {
  formatDate,
  getClientBookingRelationship,
} from "~/utils";
import { JobStatus, SortOrder } from "~/utils/constants";
import { useAuth } from "~/store";
import { useGetFiles } from "~/services/file";
import { ContainerInfoCard } from "~/components/common/container-info";

const { Text } = Typography;

interface BookingDetailsModalProps {
  booking: TBooking;
  onClose: () => void;
}

interface ServiceWithPricing extends TServiceTicket {
  estimatedCost?: number;
}

export const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
  booking,
}) => {
  const { user } = useAuth();
  const currentClientId = user?.client?.id;

  const { data: documentsData, isLoading } = useGetFiles({
    jobTicketId: booking?.id,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Determine client relationship and filter data accordingly
  const relationshipData = useMemo(() => {
    if (!currentClientId) return null;
    return getClientBookingRelationship(booking, currentClientId);
  }, [booking, currentClientId]);

  // Use filtered service tickets based on client relationship
  const visibleServiceTickets = useMemo(() => {
    if (!relationshipData) return booking.serviceTicket || [];

    return relationshipData.relationship === "service-client"
      ? relationshipData.visibleServiceTickets
      : booking.serviceTicket || [];
  }, [booking.serviceTicket, relationshipData]);

  const servicesWithPricing = useMemo((): ServiceWithPricing[] => {
    return visibleServiceTickets.map((service) => ({
      ...service,
      estimatedCost:
        service.supplies?.reduce((total, supply) => {
          return total + (supply.cost || 0);
        }, 0) || 0,
    }));
  }, [visibleServiceTickets]);

  const totalEstimatedCost = useMemo(() => {
    return servicesWithPricing.reduce((total, service) => {
      return total + (service.estimatedCost || 0);
    }, 0);
  }, [servicesWithPricing]);

  const getStatusBadge = (status: JobStatus) => {
    // In Progress is special - just italic black text, no box
    if (status === JobStatus.IN_PROGRESS) {
      return (
        <span className="text-[#1a1a2e] italic font-bold text-xs uppercase">
          In Progress
        </span>
      );
    }
    
    // Scheduled is special - just black text, no box, no italics
    if (status === JobStatus.SCHEDULED) {
      return (
        <span className="text-[#1a1a2e] font-bold text-xs uppercase">
          Scheduled
        </span>
      );
    }
    
    const statusConfig = {
      [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]" },
      [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white" },
      [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]" },
      [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]" },
    };
    const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]" };
    return (
      <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${config.className}`}>
        {config.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Service Client Notice */}
      {relationshipData?.relationship === "service-client" && (
        <Card size="small" className="border-blue-200 bg-blue-50">
          <div className="flex items-center space-x-2">
            <Tag color="blue">Service Client</Tag>
            <Text type="secondary" className="text-sm">
              You have limited access to this booking as a service client. Only
              relevant service information is shown.
            </Text>
          </div>
        </Card>
      )}

      {/* Booking Overview */}
      <Card size="small" title="Booking Overview">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Booking ID">
            <Text strong>{booking.referenceNumber}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {getStatusBadge(booking.status)}
          </Descriptions.Item>
          <Descriptions.Item label="Project">
            {booking.project?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Date Created">
            {formatDate(booking.bookingDate)}
          </Descriptions.Item>
          {relationshipData?.relationship !== "service-client" && (
            <Descriptions.Item label="Client">
              {booking.client?.companyName ||
                `${booking.client?.firstName || ""} ${
                  booking.client?.lastName || ""
                }`.trim() ||
                "N/A"}
            </Descriptions.Item>
          )}
          {booking.payment?.total && (
            <Descriptions.Item label="Total Cost">
              <Space>
                <DollarSign size={14} />
                <Text strong>${booking.payment.total.toFixed(2)}</Text>
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Services Details */}
      <Card size="small" title="Services">
        <List
          dataSource={servicesWithPricing}
          renderItem={(service) => (
            <List.Item key={service.id}>
              <ContainerInfoCard 
                serviceTicket={service} 
                variant="detailed"
                className="w-full"
              />
            </List.Item>
          )}
        />
      </Card>

      {/* Documents Section */}
      <Card size="small" title="Documents">
        {isLoading ? (
          <Text type="secondary">Loading documents...</Text>
        ) : documentsData && documentsData.items.length > 0 ? (
          <List
            size="small"
            dataSource={documentsData.items}
            renderItem={(doc) => (
              <List.Item>
                <div className="flex items-center justify-between w-full">
                  <Space>
                    <FileText size={14} className="text-gray-400" />
                    <Text>{doc.title || "Unnamed Document"}</Text>
                  </Space>
                  <Text type="secondary" className="text-xs">
                    {formatDate(doc.createdAt)}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        ) : (
          <Text type="secondary">No documents available.</Text>
        )}
      </Card>

      {/* Completion Status Information */}
      {booking.status === JobStatus.COMPLETED && (
        <Card size="small" title="Completion Information">
          <div className="space-y-2">
            {servicesWithPricing.some((s) => s.completed_at) && (
              <div>
                <Text strong>Completion Date: </Text>
                <Text>
                  {formatDate(
                    servicesWithPricing.find((s) => s.completed_at)
                      ?.completed_at ?? ""
                  )}
                </Text>
              </div>
            )}
            {servicesWithPricing.some((s) => s.clientSignature) && (
              <div>
                <Badge status="success" text="Client signature received" />
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};